import { useCallback, useEffect, useRef } from 'react';

/**
 * Emoji data and the caret plumbing a picker needs.
 *
 * ## Why the data comes from a package and the UI does not
 *
 * There are 1,914 emoji, each needing a name and keywords to be searchable.
 * That is not a list anybody should hand-maintain, and getting it subtly wrong
 * means somebody cannot find the one they want.
 *
 * But the picker *components* on npm all bring their own styling, their own
 * dark-mode assumptions and their own idea of a focus ring, and fighting those
 * back into this design system costs more than writing the grid. So: their
 * data, our interface.
 *
 * `unicode-emoji-json` is data only - no React, no CSS, no runtime.
 *
 * ## And it is loaded on demand
 *
 * 1,914 entries with names is a few hundred kilobytes, and it sat in the main
 * bundle - so somebody opening a chart downloaded every emoji on the way. It is
 * behind a dynamic `import()` now, fetched the first time a picker opens and
 * cached for the rest of the session.
 *
 * That is the difference between a feature that costs everybody and one that
 * costs the people using it.
 */

export interface Emoji {
  char: string;
  name: string;
  slug: string;
  group: string;
}

/**
 * The categories, in the order they are offered.
 *
 * The icon is the tab's label to a person who can see it; `label` is what a
 * screen reader gets, because a picture of a bear is not the word "animals".
 */
export const CATEGORIES: { group: string; icon: string; label: string }[] = [
  { group: 'Smileys & Emotion', icon: '😀', label: 'Smileys and emotion' },
  { group: 'People & Body', icon: '👋', label: 'People and body' },
  { group: 'Animals & Nature', icon: '🐻', label: 'Animals and nature' },
  { group: 'Food & Drink', icon: '🍕', label: 'Food and drink' },
  { group: 'Activities', icon: '⚽', label: 'Activities' },
  { group: 'Travel & Places', icon: '✈️', label: 'Travel and places' },
  { group: 'Objects', icon: '💡', label: 'Objects' },
  { group: 'Symbols', icon: '❤️', label: 'Symbols' },
  { group: 'Flags', icon: '🏁', label: 'Flags' },
];

/** The loaded set, once it has arrived. */
export interface EmojiSet {
  all: Emoji[];
  byGroup: Map<string, Emoji[]>;
  byChar: Map<string, Emoji>;
}

let cached: EmojiSet | null = null;
let inFlight: Promise<EmojiSet> | null = null;

/**
 * Fetch the emoji data, once per session.
 *
 * The promise is memoised as well as the result: two pickers opening in the
 * same tick would otherwise each start a download, and the second would be
 * wasted.
 */
export function loadEmoji(): Promise<EmojiSet> {
  if (cached !== null) return Promise.resolve(cached);

  inFlight ??= import('unicode-emoji-json').then((module) => {
    const raw = module.default as Record<string, { name: string; slug: string; group: string }>;

    const all: Emoji[] = Object.entries(raw).map(([char, meta]) => ({
      char,
      name: meta.name,
      slug: meta.slug,
      group: meta.group,
    }));

    // The indexes are built here rather than per-render: they are the same
    // every time, and rebuilding them on each keystroke of a search box is the
    // one thing that would make this feel slow.
    const byGroup = new Map<string, Emoji[]>();
    const byChar = new Map<string, Emoji>();

    for (const emoji of all) {
      byChar.set(emoji.char, emoji);

      const list = byGroup.get(emoji.group);

      if (list) {
        list.push(emoji);
      } else {
        byGroup.set(emoji.group, [emoji]);
      }
    }

    cached = { all, byGroup, byChar };

    return cached;
  });

  return inFlight;
}

/**
 * Search by name.
 *
 * Word-prefix rather than substring: searching "car" should find "car" and
 * "card index" before "scarf", and a plain `includes` puts them in whatever
 * order the dataset happens to be in. Exact-name matches come first because
 * somebody typing the whole word knows what they want.
 */
export function searchEmoji(set: EmojiSet, query: string, limit = 120): Emoji[] {
  const needle = query.trim().toLowerCase();

  if (needle === '') return [];

  const exact: Emoji[] = [];
  const prefix: Emoji[] = [];
  const loose: Emoji[] = [];

  for (const emoji of set.all) {
    if (emoji.name === needle) {
      exact.push(emoji);
      continue;
    }

    // `slug` is underscore-joined, so splitting it gives the words without
    // having to tokenise the display name's punctuation.
    const words = emoji.slug.split('_');

    if (words.some((word) => word.startsWith(needle))) {
      prefix.push(emoji);
      continue;
    }

    if (emoji.name.includes(needle)) {
      loose.push(emoji);
    }
  }

  return [...exact, ...prefix, ...loose].slice(0, limit);
}

/* -------------------------------------------------------------------------- */
/* Recently used                                                               */
/* -------------------------------------------------------------------------- */

const RECENT_KEY = 'gv.emoji.recent';
const RECENT_MAX = 24;

/**
 * The last few used, per browser.
 *
 * Local rather than on the account: it is a convenience, not a preference, and
 * synchronising it would mean a write to the server on every emoji anybody
 * picks. Wrapped in try/catch because storage throws outright in a private
 * window in some browsers - and losing the recents list is not worth an
 * exception reaching a composer.
 */
export function recentEmoji(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);

    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(char: string): string[] {
  const next = [char, ...recentEmoji().filter((c) => c !== char)].slice(0, RECENT_MAX);

  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // See above. The list is a nicety; failing to save one is not an error.
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/* Inserting into a composer                                                   */
/* -------------------------------------------------------------------------- */

type Composer = HTMLInputElement | HTMLTextAreaElement;

/**
 * Wire a picker to a controlled input or textarea.
 *
 * ## Inserting at the caret, not appending
 *
 * Appending is the obvious implementation and it is wrong the first time
 * somebody puts the cursor mid-sentence. This splices at the current selection,
 * replacing it if there is one - the same thing typing a character would do.
 *
 * ## Why the caret is restored in an effect
 *
 * The value is controlled by React, so the DOM does not hold the new text until
 * after the re-render. Setting `selectionRange` immediately would place the
 * caret in the *old* string, and the browser would then clamp or move it when
 * the value changed. So the target offset is stashed and applied once the
 * render that carries the new value has committed.
 */
export function useEmojiComposer(
  value: string,
  onChange: (next: string) => void,
): { ref: React.RefObject<Composer | null>; insert: (char: string) => void } {
  const ref = useRef<Composer | null>(null);
  const caret = useRef<number | null>(null);

  useEffect(() => {
    if (caret.current === null) return;

    const element = ref.current;

    if (element) {
      // Focus goes back to the composer, so picking an emoji does not end with
      // the cursor nowhere and the next keystroke lost.
      element.focus();
      element.setSelectionRange(caret.current, caret.current);
    }

    caret.current = null;
  }, [value]);

  const insert = useCallback(
    (char: string) => {
      const element = ref.current;
      const start = element?.selectionStart ?? value.length;
      const end = element?.selectionEnd ?? value.length;

      caret.current = start + char.length;
      onChange(value.slice(0, start) + char + value.slice(end));
      rememberEmoji(char);
    },
    [value, onChange],
  );

  return { ref, insert };
}
